import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Container from "@saleor/components/Container";
import FilterBar, { CustomAction } from "@saleor/components/FilterBar";
import Form from "@saleor/components/Form";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import { SubmitPromise } from "@saleor/hooks/useForm";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { SearchBrandsPrivate_search_edges_node } from "@saleor/searches/types/SearchBrandsPrivate";
import {
  FetchMoreProps,
  FilterPageProps,
  PageListProps,
  TabPageProps
} from "@saleor/types";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { TenantList_tenantsPrivate_edges_node } from "../../types/TenantList";
import StoreList from "../StoreList";
import {
  createFilterStructure,
  StoreListUrlFiltersOpts,
  TenantFilterKeys
} from "./filters";
import SellerBrandDropdown from "./SellerBrandDropdown";

export interface SellerBrandFormData {
  sellerBrand: string;
}

export interface StoreListProps
  extends PageListProps,
    TabPageProps,
    FilterPageProps<TenantFilterKeys, StoreListUrlFiltersOpts> {
  /** Identifiers */
  brandID: string;
  brandLabel: string;

  /** Data */
  brands: SearchBrandsPrivate_search_edges_node[];
  tenants: TenantList_tenantsPrivate_edges_node[];

  /** Fetch actions */
  fetchBrands: (query: string) => void;
  fetchMoreBrands?: FetchMoreProps;

  /** Custom Event handlers */
  onChangeBrand: (brandId: string) => void;
  onSubmit: (data) => SubmitPromise;

  /** Customization & state */
  customAction: CustomAction;
  hasManageTenantPermission: boolean;
}

const StoreListPage: React.FC<StoreListProps> = props => {
  const {
    /** Identifiers */
    brandID,
    brandLabel,

    /** Data */
    brands,
    // tenants,

    /** Fetch actions */
    fetchBrands,
    fetchMoreBrands,

    /** Custom Event handlers */
    onChangeBrand,
    onSubmit,

    /** Customization & state */
    customAction,
    hasManageTenantPermission,

    /** Event handlers */
    onSearchChange,
    onFilterChange,
    onAll,
    onTabChange,
    onTabDelete,
    onTabSave,
    onAdd,

    /** Tabs & filters */
    currentTab,
    filterOpts,
    initialSearch,
    tabs,
    currencySymbol,

    /** Rest */
    ...listProps
  } = props;
  const intl = useIntl();
  const { user } = useUser();
  const structure = createFilterStructure(user.userType.code, intl, filterOpts);

  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");

  const sellerBrandChoices = maybe(
    () =>
      brands.map(brand => ({
        label: brand?.name + " - " + brand.legalName,
        value: brand?.id
      })),
    []
  );

  useEffect(() => {
    setSellerDisplayName(brandLabel);
  }, [brandLabel]);

  const initialForm: SellerBrandFormData = {
    sellerBrand: ""
  };

  const breadcrumbDirectory = [
    {
      name: "Stores"
    }
  ];

  return (
    <Form initial={initialForm} confirmLeave>
      {({ data: formData, change, submit }) => {
        const handleSelectBrand = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          sellerBrandChoices
        );

        return (
          <Container>
            {user.userType.code === SmopUserTypeCode.ACCOUNT_MANAGER && (
              <div style={{ display: "flex" }}>
                <div style={{ marginLeft: "auto", order: 2, width: "300px" }}>
                  <SellerBrandDropdown
                    brandID={brandID}
                    brands={sellerBrandChoices}
                    data={formData}
                    change={change}
                    fetchBrands={query => fetchBrands(query)}
                    fetchMoreBrands={fetchMoreBrands}
                    onBrandChange={event => {
                      handleSelectBrand(event);
                      onChangeBrand(event.target.value);
                    }}
                    sellerDisplayValue={sellerDisplayName}
                    submit={submit}
                  />
                </div>
              </div>
            )}
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <PageHeader
              title={intl.formatMessage({ defaultMessage: "Stores" })}
            >
              {user.userType.code !== SmopUserTypeCode.SELLER_MASTER &&
                user.userType.code !== SmopUserTypeCode.STORE_ADMIN && (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={onAdd}
                    id="createStoreBtn"
                    disabled={!hasManageTenantPermission}
                  >
                    <FormattedMessage
                      defaultMessage="Create Store"
                      description="button"
                    />
                  </Button>
                )}
            </PageHeader>
            <Card>
              <FilterBar
                currencySymbol={currencySymbol}
                currentTab={currentTab}
                tabs={tabs}
                filterStructure={structure}
                searchPlaceholder={intl.formatMessage({
                  defaultMessage: "Search Mall, Seller"
                })}
                initialSearch={initialSearch}
                onSearchChange={onSearchChange}
                onFilterChange={onFilterChange}
                onTabChange={onTabChange}
                onTabDelete={onTabDelete}
                onTabSave={onTabSave}
                disablePlusOneTab={true}
                hideAction={true}
                customAction={customAction}
              />
              <StoreList
                onSubmit={onSubmit}
                hasManageTenantPermission={hasManageTenantPermission}
                {...listProps}
              />
            </Card>
          </Container>
        );
      }}
    </Form>
  );
};
StoreListPage.displayName = "StoreListPage";
export default StoreListPage;
